import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import MapParcelPicker from "./MapParcelPicker";

const API_KEY =
  "88fc34912a9b6e430657880fb073992ad2e43de494a6ff9673bc14c12caf09ff";

const Num = ({ n }) => <span className="lnum">{n}</span>;

function App() {
  const LONG_TIMEOUT_MS = 600_000;

  const FRIENDLY_PARCEL_ERROR = "Nie udało nam się zebrać danych o działce.";

  const INITIAL_FORM = {
    parcelNo: "",
    municipality: "",
    obreb: "",
    postalCode: "",
    name: "",
    phone: "",
    mail: "",
    cena_m2: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [pp, setPP] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const requestAbortRef = useRef(null);

  const fetchWithTimeout = async (
    url,
    options = {},
    timeoutMs,
    externalSignal
  ) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    const onAbort = () => controller.abort();
    if (externalSignal)
      externalSignal.addEventListener("abort", onAbort, { once: true });
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } finally {
      clearTimeout(id);
      if (externalSignal) externalSignal.removeEventListener("abort", onAbort);
    }
  };

  useEffect(() => {
    const onOffline = () => setError(FRIENDLY_PARCEL_ERROR);
    window.addEventListener("offline", onOffline);
    return () => window.removeEventListener("offline", onOffline);
  }, []);

  const handleParcelFromMap = ({
    parcelNo,
    municipality,
    obreb,
    postalCode,
  }) => {
    setFormData((prev) => ({
      ...prev,
      parcelNo: parcelNo || "",
      municipality: municipality || "",
      obreb: obreb || "",
      postalCode: postalCode || "",
    }));
    setError(null);
    setSuccess(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fire & forget — nie czekamy na odpowiedź serwera
  const fireAndForget = (payload) => {
    if (requestAbortRef.current) {
      try {
        requestAbortRef.current.abort();
      } catch {}
    }
    const controller = new AbortController();
    requestAbortRef.current = controller;

    fetchWithTimeout(
      "https://sluzebnosckppp.ftpeople.pl/mapka",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
        body: JSON.stringify(payload),
      },
      LONG_TIMEOUT_MS,
      controller.signal
    )
      .then(async (res) => {
        if (!res.ok) console.warn("[mapka] server error", res.status);
        else {
          const data = await res.json().catch(() => null);
          console.log("[mapka] done", data?.parcelId);
        }
      })
      .catch((err) => {
        if (err?.name !== "AbortError")
          console.warn("[mapka] fetch error", err.message);
      })
      .finally(() => {
        if (requestAbortRef.current === controller)
          requestAbortRef.current = null;
      });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.parcelNo?.trim() || !formData.municipality?.trim()) {
      setError("Wybierz działkę na mapie przed wysłaniem formularza.");
      return;
    }
    if (formData.cena_m2) {
      const val = Number(formData.cena_m2);
      if (isNaN(val) || val <= 0) {
        setError("Orientacyjna cena m² musi być liczbą większą od 0.");
        return;
      }
    }

    const parsedCena = formData.cena_m2 ? Number(formData.cena_m2) : null;
    const payload = {
      municipality: formData.municipality,
      parcelNo: formData.parcelNo,
      obreb: formData.obreb,
      postalCode: formData.postalCode,
      name: formData.name,
      phone: formData.phone,
      mail: formData.mail,
      mode: "parcel",
      half: 100,
      ...(parsedCena && parsedCena > 0 ? { cena_m2: parsedCena } : {}),
    };

    // Wyślij w tle, natychmiast resetuj formularz
    fireAndForget(payload);
    setFormData(INITIAL_FORM);
    setPP(true);
    setError(null);
    setSuccess(true);
    setLoading(false);
  };

  const parcelSelected = !!formData.parcelNo && !!formData.municipality;

  return (
    <>
      <section className="input-section">
        {success && (
          <div className="success-banner">
            <svg
              viewBox="0 0 20 20"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ flexShrink: 0 }}
            >
              <path d="M10 2a8 8 0 1 1 0 16A8 8 0 0 1 10 2z" />
              <path d="M7 10l2 2 4-4" />
            </svg>
            Zgłoszenie przyjęte! Analiza zostanie wysłana na podany adres
            e-mail.
          </div>
        )}

        <form onSubmit={handleSubmit} className="glass-card">
          {/* ─── MAPA ─── */}
          <div className="field" style={{ flex: "1 1 100%" }}>
            <MapParcelPicker
              onParcelSelected={handleParcelFromMap}
              disabled={loading}
            />
          </div>

          {parcelSelected && (
            <div className="parcel-selected-notice">
              <svg
                viewBox="0 0 20 20"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ flexShrink: 0 }}
              >
                <path d="M10 2a8 8 0 1 1 0 16A8 8 0 0 1 10 2z" />
                <path d="M7 10l2 2 4-4" />
              </svg>
              Działka <strong>{formData.parcelNo}</strong> w miejscowości{" "}
              <strong>{formData.municipality}</strong>
              {formData.obreb ? ` (obręb: ${formData.obreb})` : ""}
              {formData.postalCode ? `, ${formData.postalCode}` : ""}
            </div>
          )}

          {/* ─── CENA M2 (opcjonalna) ─── */}
          <div className="input-row">
            <div className="field">
              <label>
                <Num n="①" /> Cena 1&nbsp;m² gruntu &nbsp;
                <span className="label-optional">(opcjonalnie)</span>
              </label>
              <div className="input-with-suffix">
                <input
                  name="cena_m2"
                  type="number"
                  min="1"
                  step="1"
                  value={formData.cena_m2}
                  onChange={handleChange}
                  placeholder="np. 120"
                  disabled={loading}
                />
                <span className="input-suffix">zł/m²</span>
              </div>
              <span className="span_info">
                Jeśli znasz lokalną cenę gruntu, wpisz ją — analiza będzie
                dokładniejsza. Pozostaw puste, aby system wyznaczył cenę
                automatycznie.
              </span>
            </div>
          </div>

          {/* ─── DANE OSOBOWE ─── */}
          <div className="input-row">
            <div className="field">
              <label>
                <Num n="②" /> Imię i nazwisko *
              </label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Jan Kowalski"
                required
                disabled={loading}
              />
            </div>
            <div className="field">
              <label>
                <Num n="③" /> Numer telefonu *
              </label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="123 123 123"
                required
                disabled={loading}
              />
            </div>
            <div className="field">
              <label>
                <Num n="④" /> Adres e-mail *
              </label>
              <input
                name="mail"
                value={formData.mail}
                onChange={handleChange}
                placeholder="jan@kowalski.pl"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* ─── POLITYKA ─── */}
          <div className="input-row">
            <div className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={pp}
                  onChange={(e) => setPP(e.target.checked)}
                  required
                  disabled={loading}
                />
                zapoznałem/-am się z
                <a
                  href="https://ilezaslup.pl/polityka-prywatnosci/"
                  target="_blank"
                  rel="noreferrer"
                  style={{ paddingLeft: "3px", paddingRight: "3px" }}
                >
                  polityką prywatności
                </a>
                i akceptuję jej postanowienia.
              </label>
            </div>
          </div>

          {/* ─── SUBMIT ─── */}
          <div>
            <button
              className="submit-button"
              disabled={loading || !parcelSelected}
            >
              {parcelSelected ? "Dalej" : "Wybierz działkę na mapie"}
            </button>
            <p
              style={{
                marginTop: "10px",
                fontSize: "0.75rem",
                color: "#9ab8b8",
              }}
            >
              * pole obowiązkowe
            </p>
          </div>
        </form>

        {error && <div className="error-msg">{error}</div>}
      </section>
    </>
  );
}

export default App;
